import { createAction } from '@reduxjs/toolkit'

import { DepositResponseNames, PaymentTypesType } from 'services/api/PaymentApi/types/common'
import {
  ICashierItem,
  ICryptoCurrencyWithMinAndMaxItem,
  ICryptoEstimateAmount,
  ICryptoMinAmount,
  IDefaultFilters,
  IDeposit,
  IGetCryptoEstimateAmountPayload,
  IRequestCotePayout,
  IUploadDocumentPayload,
} from 'services/api/PaymentApi/types/types'

import { defaultCurrency } from 'shared/constants'
import { LocaleErrorStatusMessageKeysType } from 'shared/locale/en-US'
import { IGetCryptoMinAmountPayload } from 'shared/models/Crypto'
import { IPaymentPayload, UploadScreenshotResponse } from 'shared/models/Payments'
import { makeCommunicationActionCreator } from 'shared/utils/communication/actions/makeCommunicationActionCreator'
// eslint-disable-next-line max-len
import { makeCommunicationByIdActionCreator } from 'shared/utils/communication/actions/makeCommunicationByIdActionCreator'

import { addNotify } from 'components/Notify'

import { ISystemListState } from './types'

export const clearDepositCreated = createAction('CLEAR_DEPOSIT_CREATED')

export const clearConvertedAmountData = createAction('CLEAR_CONVERT_AMOUNT_DATA')

export const addScreenshotsData = createAction<{
  data: UploadScreenshotResponse[]
}>('ADD_SCREENSHOTS_DATA')

export const clearScreenshotsData = createAction('CLEAR_SCREENSHOTS_DATA')

export const getDepositSystemList = makeCommunicationActionCreator({
  loading: '@payment/GET_DEPOSIT_SYSTEM_LIST_LOADING',
  success: '@payment/GET_DEPOSIT_SYSTEM_LIST_SUCCESS',
  error: '@payment/GET_DEPOSIT_SYSTEM_LIST_ERROR',
  reset: '@payment/GET_DEPOSIT_SYSTEM_LIST_RESET',
})<IDefaultFilters, ISystemListState>(
  async ({
    deps: {
      extra: { api },
    },
    payload,
  }) => {
    const response = await api.payment.getInvoiceSystemList(payload)
    return response.data
  },
)

export const createDeposit = makeCommunicationByIdActionCreator({
  loading: '@payment/CREATE_DEPOSIT_LOADING',
  success: '@payment/CREATE_DEPOSIT_SUCCESS',
  error: '@payment/CREATE_DEPOSIT_ERROR',
  reset: '@payment/CREATE_DEPOSIT_RESET',
})<IPaymentPayload, IDeposit>(
  async ({
    deps: {
      extra: { api },
      getState,
      dispatch,
    },
    payload,
  }) => {
    const { files, ...data } = payload

    const { errorStatusMessage } = getState().locale.data

    const response = await api.payment.setDeposit(data)

    if (response.success) {
      let uploadResponse
      if (files?.length && response.data.externalId) {
        const formData = new FormData()
        files.forEach(file => {
          formData.append('deposit_images', file)
        })

        uploadResponse = await api.payment.uploadPaymentScreenshot(formData, response.data.externalId)
        dispatch(addScreenshotsData({ data: uploadResponse.data }))
      }
      if (uploadResponse?.data.some(sc => sc.errorMessage))
        uploadResponse?.data.forEach(sc => {
          if (sc.errorMessage) {
            addNotify(errorStatusMessage[sc.status as LocaleErrorStatusMessageKeysType] ?? sc.errorMessage, 'error')
          }
        })
      else if (response.data.type === DepositResponseNames.EMPTY && payload?.successCallback) payload.successCallback()
      else if (response.data.redirectLink) location.replace(response.data.redirectLink)
    }
    return { id: payload.id, ...response.data }
  },
)

export const createPayout = makeCommunicationActionCreator({
  loading: '@payment/CREATE_PAYOUT_LOADING',
  success: '@payment/CREATE_PAYOUT_SUCCESS',
  error: '@payment/CREATE_PAYOUT_ERROR',
  reset: '@payment/CREATE_PAYOUT_RESET',
})<IPaymentPayload, void>(
  async ({
    deps: {
      extra: { api },
      getState,
    },
    payload,
  }) => {
    // const locale = getState().locale.data.common

    const response = await api.payment.setPayout(payload)

    if (response.success) {
      if (payload?.successCallback) {
        payload.successCallback()
      }
    }
  },
)

export const getCryptoCurrenciesList = makeCommunicationActionCreator({
  loading: '@payment/GET_CRYPTO_CURRENCIES_LIST_LOADING',
  success: '@payment/GET_CRYPTO_CURRENCIES_LIST_SUCCESS',
  error: '@payment/GET_CRYPTO_CURRENCIES_LIST_ERROR',
  reset: '@payment/GET_CRYPTO_CURRENCIES_LIST_RESET',
})<void, ICryptoCurrencyWithMinAndMaxItem[]>(
  async ({
    deps: {
      extra: { api },
    },
  }) => {
    const response = await api.payment.getCryptoCurrencies()
    return response.data
  },
)

export const getCryptoMinAmount = makeCommunicationByIdActionCreator({
  loading: '@payment/GET_CRYPTO_MIN_AMOUNT_LOADING',
  success: '@payment/GET_CRYPTO_MIN_AMOUNT__SUCCESS',
  error: '@payment/GET_CRYPTO_MIN_AMOUNT_ERROR',
  reset: '@payment/GET_CRYPTO_MIN_AMOUNT_RESET',
})<IGetCryptoMinAmountPayload, ICryptoMinAmount>(
  async ({
    deps: {
      extra: { api },
      getState,
    },
    payload,
  }) => {
    const currency = getState().auth.data.user?.currency || defaultCurrency

    const response = await api.payment.getCryptoMinAmount({ id: payload.id, fiat_currency: currency })

    return { id: response.data.currency, ...response.data }
  },
)

export const getCryptoEstimateAmount = makeCommunicationActionCreator({
  loading: '@payment/GET_CRYPTO_ESTIMATE_AMOUNT_LOADING',
  success: '@payment/GET_CRYPTO_ESTIMATE_AMOUNT__SUCCESS',
  error: '@payment/GET_CRYPTO_ESTIMATE_AMOUNT_ERROR',
  reset: '@payment/GET_CRYPTO_ESTIMATE_AMOUNT_RESET',
})<IGetCryptoEstimateAmountPayload, ICryptoEstimateAmount, { callback?: () => void }>(
  async ({
    deps: {
      extra: { api },
    },
    payload,
  }) => {
    const response = await api.payment.getCryptoEstimateAmount(payload)

    return response.data
  },
)

export const getPayoutSystemList = makeCommunicationActionCreator({
  loading: '@payment/GET_PAYOUT_SYSTEM_LIST_LOADING',
  success: '@payment/GET_PAYOUT_SYSTEM_LIST_SUCCESS',
  error: '@payment/GET_PAYOUT_SYSTEM_LIST_ERROR',
  reset: '@payment/GET_PAYOUT_SYSTEM_LIST_RESET',
})<IDefaultFilters, ISystemListState>(
  async ({
    deps: {
      extra: { api },
    },
    payload,
  }) => {
    const response = await api.payment.getPayoutSystemList(payload)
    return response.data
  },
)

export const cancelPayment = makeCommunicationByIdActionCreator({
  loading: '@profile/CANCEL_PAYMENT_LOADING',
  success: '@profile/CANCEL_PAYMENT_SUCCESS',
  error: '@profile/CANCEL_PAYMENT_ERROR',
  reset: '@profile/CANCEL_PAYMENT_RESET',
})<{ id: string; paymentType: PaymentTypesType; successCallback?: () => void }, { id: string }>(
  async ({
    deps: {
      extra: { api },
    },
    payload,
  }) => {
    try {
      await api.payment.cancelPayment(payload.id, payload.paymentType)

      if (payload?.successCallback) {
        payload.successCallback()
      }
    } catch (e) {
      console.error(e)
    }

    return { id: payload.id }
  },
)

export const getCashiersList = makeCommunicationActionCreator({
  loading: '@payment/GET_CASHIER_LIST_LOADING',
  success: '@payment/GET_CASHIER_LIST_SUCCESS',
  error: '@payment/GET_CASHIER_LIST_ERROR',
  reset: '@payment/GET_CASHIER_LIST_RESET',
})<void, ICashierItem[]>(
  async ({
    deps: {
      extra: { api },
    },
  }) => {
    const response = await api.payment.getCashiersList()
    return response.data
  },
)

export const requestCotePayout = makeCommunicationActionCreator({
  loading: '@payment/REQUEST_COTE_PAYOUT_LOADING',
  success: '@payment/REQUEST_COTE_PAYOUT_SUCCESS',
  error: '@payment/REQUEST_COTE_PAYOUT_ERROR',
  reset: '@payment/REQUEST_COTE_PAYOUT_RESET',
})<IRequestCotePayout, void, { callback?: () => void }>(
  async ({
    deps: {
      extra: { api },
    },
    payload,
    args,
  }) => {
    const response = await api.payment.requestCotePayout(payload)

    if (response.success) {
      if (args?.callback) {
        args.callback()
      }
    }
  },
)

export const uploadDocument = makeCommunicationActionCreator({
  loading: '@payment/UPLOAD_DOCUMENT_LOADING',
  success: '@payment/UPLOAD_DOCUMENT_SUCCESS',
  error: '@payment/UPLOAD_DOCUMENT_ERROR',
  reset: '@payment/UPLOAD_DOCUMENT_RESET',
})<IUploadDocumentPayload[], void, { callback: () => void }>(
  async ({
    deps: {
      extra: { api },
      getState,
    },
    payload,
    args,
  }) => {
    const locale = getState().locale.data.profile

    const forms = [...payload]

    try {
      const response = await Promise.all(
        forms.map(async formData => {
          const resSub = await api.payment.uploadDocument(formData)
          return resSub
        }),
      )

      const isSuccess = response && !response.some(r => !r?.success)

      if (isSuccess) {
        // addNotify(locale.kycDocumentSent, 'success')
        args?.callback?.()
      }
    } catch (e) {
      console.error(e)
    }
  },
)
